import { Component } from '@theme/component';
import { debounce, onAnimationEnd, prefersReducedMotion, onDocumentLoaded } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';
import { morph } from '@theme/morph';
import { RecentlyViewed } from '@theme/recently-viewed-products';
import { DialogCloseEvent, DialogComponent } from '@theme/dialog';

/**
 * A custom element that allows the user to search for resources available on the store.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchInput - The search input element.
 * @property {HTMLElement} predictiveSearchResults - The predictive search results container.
 * @property {HTMLElement} resetButton - The reset button element.
 * @property {HTMLElement[]} [resultsItems] - The search results items elements.
 * @property {HTMLElement} [recentlyViewedWrapper] - The recently viewed products wrapper.
 * @property {HTMLElement[]} [recentlyViewedTitle] - The recently viewed title elements.
 * @property {HTMLElement[]} [recentlyViewedItems] - The recently viewed product items.
 * @extends {Component<Refs>}
 */
class PredictiveSearchComponent extends Component {
  requiredRefs = ['searchInput', 'predictiveSearchResults', 'resetButton'];

  #controller = new AbortController();
  /** @type {Array<any>} */
  #localProducts = [];
  #localProductsLoaded = false;

  /**
   * @type {AbortController | null}
   */
  #activeFetch = null;

  /**
   * Get the dialog component.
   * @returns {DialogComponent | null} The dialog component.
   */
  get dialog() {
    return this.closest('dialog-component');
  }

  connectedCallback() {
    super.connectedCallback();

    const { dialog } = this;
    const { signal } = this.#controller;

    if (this.refs.searchInput.value.length > 0) {
      this.#showResetButton();
    }

    if (dialog) {
      document.addEventListener('keydown', this.#handleKeyboardShortcut, { signal });
      dialog.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose, { signal });

      this.addEventListener('click', this.#handleModalClick, { signal });
    }

    onDocumentLoaded(() => {
      this.resetSearch(false); // Pass false to avoid focusing the input
    });
  }

  /**
   * Handles clicks within the predictive search modal to maintain focus on the input
   * @param {MouseEvent} event - The mouse event
   */
  #handleModalClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const isInteractiveElement =
      target instanceof HTMLButtonElement ||
      target instanceof HTMLAnchorElement ||
      target instanceof HTMLInputElement ||
      target.closest('button') ||
      target.closest('a') ||
      target.closest('input');

    if (!isInteractiveElement && this.refs.searchInput) {
      this.refs.searchInput.focus();
    }
  };

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  /**
   * Handles the CMD+K key combination.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyboardShortcut = (event) => {
    if (event.metaKey && event.key === 'k') {
      this.dialog?.toggleDialog();
    }
  };

  /**
   * Handles the dialog close event.
   */
  #handleDialogClose = () => {
    this.#resetSearch();
  };

  get #allResultsItems() {
    const containers = Array.from(
      this.querySelectorAll(
        '.predictive-search-results__wrapper-queries, ' +
        '.predictive-search-results__wrapper-products, ' +
        '.predictive-search-results__list'
      )
    );

    const allItems = containers
      .flatMap((container) => {
        if (container.classList.contains('predictive-search-results__wrapper-products')) {
          return Array.from(container.querySelectorAll('.predictive-search-results__card'));
        }
        return Array.from(container.querySelectorAll('[ref="resultsItems[]"], .predictive-search-results__card'));
      })
      .filter((item) => item instanceof HTMLElement);

    return /** @type {HTMLElement[]} */ (allItems);
  }

  /**
   * Track whether the last interaction was keyboard-based
   * @type {boolean}
   */
  #isKeyboardNavigation = false;

  get #currentIndex() {
    return this.#allResultsItems?.findIndex((item) => item.getAttribute('aria-selected') === 'true') ?? -1;
  }

  set #currentIndex(index) {
    if (!this.#allResultsItems?.length) return;

    this.#allResultsItems.forEach((item) => {
      item.classList.remove('keyboard-focus');
    });

    for (const [itemIndex, item] of this.#allResultsItems.entries()) {
      if (itemIndex === index) {
        item.setAttribute('aria-selected', 'true');

        if (this.#isKeyboardNavigation) {
          item.classList.add('keyboard-focus');
        }
        item.scrollIntoView({ behavior: prefersReducedMotion() ? 'instant' : 'smooth', block: 'nearest' });
      } else {
        item.removeAttribute('aria-selected');
      }
    }
    this.refs.searchInput.focus();
  }

  get #currentItem() {
    return this.#allResultsItems?.[this.#currentIndex];
  }

  /**
   * Navigate through the predictive search results using arrow keys or close them with the Escape key.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  onSearchKeyDown = (event) => {
    if (event.key === 'Escape') {
      this.#resetSearch();
      return;
    }

    if (!this.#allResultsItems?.length || event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      return;
    }

    const currentIndex = this.#currentIndex;
    const totalItems = this.#allResultsItems.length;

    switch (event.key) {
      case 'ArrowDown':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        break;

      case 'Tab':
        if (event.shiftKey) {
          this.#isKeyboardNavigation = true;
          event.preventDefault();
          this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        } else {
          this.#isKeyboardNavigation = true;
          event.preventDefault();
          this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        }
        break;

      case 'ArrowUp':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        break;

      case 'Enter': {
        const singleResultContainer = this.refs.predictiveSearchResults.querySelector('[data-single-result-url]');
        if (singleResultContainer instanceof HTMLElement && singleResultContainer.dataset.singleResultUrl) {
          event.preventDefault();
          window.location.href = singleResultContainer.dataset.singleResultUrl;
          return;
        }

        if (this.#currentIndex >= 0) {
          event.preventDefault();
          this.#currentItem?.querySelector('a')?.click();
        } else {
          const searchUrl = new URL(Theme.routes.search_url, location.origin);
          searchUrl.searchParams.set('q', this.refs.searchInput.value);
          window.location.href = searchUrl.toString();
        }
        break;
      }
    }
  };

  /**
   * Clears the recently viewed products.
   * @param {Event} event - The event.
   */
  clearRecentlyViewedProducts(event) {
    event.stopPropagation();

    RecentlyViewed.clearProducts();

    const { recentlyViewedItems, recentlyViewedTitle, recentlyViewedWrapper } = this.refs;

    const allRecentlyViewedElements = [...(recentlyViewedItems || []), ...(recentlyViewedTitle || [])];

    if (allRecentlyViewedElements.length === 0) {
      return;
    }

    if (recentlyViewedWrapper) {
      recentlyViewedWrapper.classList.add('removing');

      onAnimationEnd(recentlyViewedWrapper, () => {
        recentlyViewedWrapper.remove();
      });
    }
  }

  /**
   * Reset the search state.
   * @param {boolean} [keepFocus=true] - Whether to keep focus on input after reset
   */
  resetSearch = debounce((keepFocus = true) => {
    if (keepFocus) {
      this.refs.searchInput.focus();
    }
    this.#resetSearch();
  }, 100);

  /**
   * Debounce the search handler to fetch and display search results based on the input value.
   * Reset the current selection index and close results if the search term is empty.
   */
  search = debounce((event) => {
    // If the input is not a text input (like using the Escape key), don't search
    if (!event.inputType) return;

    const searchTerm = this.refs.searchInput.value.trim();
    this.#currentIndex = -1;

    if (!searchTerm.length) {
      this.#resetSearch();
      return;
    }

    this.#showResetButton();
    this.#getSearchResults(searchTerm);
  }, 200);

  /**
   * Resets scroll positions for search results containers
   */
  #resetScrollPositions() {
    requestAnimationFrame(() => {
      const resultsInner = this.refs.predictiveSearchResults.querySelector('.predictive-search-results__inner');
      if (resultsInner instanceof HTMLElement) {
        resultsInner.scrollTop = 0;
      }

      const formContent = this.querySelector('.predictive-search-form__content');
      if (formContent instanceof HTMLElement) {
        formContent.scrollTop = 0;
      }
    });
  }

  /**
   * Normalize product from API shape to a flat shape for search and render.
   * API: { title, handle, body_html, variants: [{ price, compare_at_price }], images: [{ src }], product_type, vendor, tags }
   */
  #normalizeProduct(/** @type {any} */ p) {
    const v0 = p.variants?.[0];
    const price = p.price != null ? p.price : (v0?.price ?? '');
    const compareAtPrice = p.compare_at_price != null ? p.compare_at_price : (v0?.compare_at_price ?? null);
    const featured_image = p.featured_image || p.images?.[0]?.src || '';
    const url = p.url || (p.handle ? `/products/${p.handle}` : '');
    const bodyPlain = (p.body_html || '')
      .replace(/<[^>]*>/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
    const skus = (p.variants || [])
      .map((/** @type {{ sku?: string } | null} */ v) => (v && v.sku ? String(v.sku).trim() : ''))
      .filter(Boolean);
    const sku = skus.join(' ');
    const qseedsCode = (p.metafields?.custom?.qseeds_code?.value != null)
      ? String(p.metafields.custom.qseeds_code.value)
      : (p.metafields?.custom?.qseeds_code != null ? String(p.metafields.custom.qseeds_code) : '');
    return {
      ...p,
      price: typeof price === 'string' ? price : String(price),
      compare_at_price: compareAtPrice != null && compareAtPrice !== '' ? (typeof compareAtPrice === 'string' ? compareAtPrice : String(compareAtPrice)) : null,
      featured_image,
      url,
      body_plain: bodyPlain,
      product_type: p.product_type || '',
      vendor: p.vendor || '',
      tags: Array.isArray(p.tags) ? p.tags : [],
      sku: sku || (v0 && v0.sku ? String(v0.sku) : ''),
      qseeds_code: (qseedsCode || '').trim()
    };
  }

  /**
   * Load all products from Shopify API (all pages, 250 per page)
   */
  async #loadLocalProducts() {
    if (this.#localProductsLoaded) return this.#localProducts;

    const baseUrl = 'https://allezadenkopennl.myshopify.com/products.json';
    const limit = 250;

    try {
      const allRaw = [];
      let page = 1;
      let hasMore = true;

      while (hasMore) {
        const url = `${baseUrl}?limit=${limit}&page=${page}`;
        const resp = await fetch(url, {
          credentials: 'omit',
          headers: {
            'User-Agent': 'Mozilla/5.0 (compatible; Shopify Product Search)',
            'Accept': 'application/json'
          }
        });
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        const data = await resp.json();
        const products = data.products || [];
        allRaw.push(...products);
        hasMore = products.length >= limit;
        page += 1;
      }

      this.#localProducts = allRaw.map((/** @type {any} */ p) => this.#normalizeProduct(p));
      this.#localProductsLoaded = true;
      console.log(`API search: loaded ${this.#localProducts.length} products from ${baseUrl}`);
      return this.#localProducts;
    } catch (e) {
      console.error('API search: failed to load products from Shopify API', e);

      // Fallback to local file if API fails
      try {
        const fallbackUrl = 'https://allezadenkopen.nl/products.json';
        console.log('API search: falling back to local products-data.json');
        const fallbackResp = await fetch(fallbackUrl, { credentials: 'omit' });
        if (fallbackResp.ok) {
          const fallbackData = await fallbackResp.json();
          const raw = fallbackData.products || [];
          this.#localProducts = raw.map((/** @type {any} */ p) => this.#normalizeProduct(p));
          this.#localProductsLoaded = true;
          console.log(`API search: fallback loaded ${this.#localProducts.length} products from ${fallbackUrl}`);
          return this.#localProducts;
        }
      } catch (fallbackError) {
        console.error('API search: fallback to local file also failed', fallbackError);
      }

      this.#localProducts = [];
      this.#localProductsLoaded = true;
      return this.#localProducts;
    }
  }

  /**
   * Build searchable text for a product (one string to match against)
   */
  #productSearchText(/** @type {any} */ p) {
    const title = (p.title || '').toLowerCase();
    const handle = (p.handle || '').toLowerCase().replace(/-/g, ' ');
    const body = (p.body_plain || '').toLowerCase();
    const type = (p.product_type || p.metafields?.producttype || '').toLowerCase();
    const vendor = (p.vendor || '').toLowerCase();
    const pkg = (p.metafields?.pakketsoort || '').toLowerCase();
    const tagsStr = (p.tags || []).join(' ').toLowerCase();
    const sku = (p.sku || '').toLowerCase();
    const qseedsCode = (p.qseeds_code || '').toLowerCase();
    return [title, handle, body, type, vendor, pkg, tagsStr, sku, qseedsCode].filter(Boolean).join(' ');
  }

  /**
   * Client-side search: token-based matching so "aar" and "aardbei" find all relevant products.
   * Splits query into tokens (min 2 chars), product matches if every token appears in any searchable field.
   */
  /**
   * @param {string} searchTerm
   */
  async #getClientSearchResults(searchTerm) {
    const products = await this.#loadLocalProducts();
    if (!products.length) {
      console.warn('Local search: No products loaded');
      return [];
    }

    const query = /** @type {string} */ (searchTerm || '').trim();
    if (!query) return [];

    const tokens = query
      .toLowerCase()
      .split(/\s+/)
      .map((t) => t.replace(/[^\wàáâäæçèéêëìíîïòóôöùúûüß]/g, ''))
      .filter((t) => t.length >= 2);

    if (tokens.length === 0) return [];

    const scored = products
      .map((/** @type {any} */ p) => {
        const searchText = this.#productSearchText(p);
        const title = (p.title || '').toLowerCase();
        const handle = (p.handle || '').toLowerCase();

        let allMatch = true;
        let score = 0;

        for (const token of tokens) {
          if (!searchText.includes(token)) {
            allMatch = false;
            break;
          }
          const sku = (p.sku || '').toLowerCase();
          const qseedsCode = (p.qseeds_code || '').toLowerCase();
          if (title.includes(token)) score += 10;
          else if (sku.includes(token) || qseedsCode.includes(token)) score += 8;
          else if (handle.includes(token)) score += 5;
          else score += 1;
        }

        return allMatch ? { product: p, score } : null;
      })
      .filter((/** @type {{ product: any; score: number } | null} */ x) => x !== null);

    scored.sort((a, b) => (b?.score ?? 0) - (a?.score ?? 0));

    const results = scored.map((/** @type {{ product: any; score: number } } */ x) => x.product);

    console.log(`API search: Found ${results.length} products matching "${searchTerm}"`);
    return results.slice(0, 20);
  }

  /**
   * Escape HTML and wrap case-insensitive match of term in text with <mark>.
   * @param {string} text - Original text (e.g. title)
   * @param {string} term - Search term (can be empty)
   * @returns {string} HTML-safe string with matches wrapped in <mark class="predictive-search-results__highlight">
   */
  #highlightMatch(text, term) {
    if (!text) return '';
    const safe = String(text)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
    if (!term || !term.trim()) return safe;
    const t = term.trim().replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const re = new RegExp(`(${t})`, 'gi');
    return safe.replace(re, '<mark class="predictive-search-results__highlight">$1</mark>');
  }

  /**
   * Render search results into the predictive search container
   * @param {Array<any>} results - Normalized products
   * @param {string} [searchTerm] - Current query, used to highlight matches in titles
   */
  #renderSearchResults(results, searchTerm = '') {
    const { predictiveSearchResults } = this.refs;
    if (!predictiveSearchResults) return;

    if (!results.length) {
      predictiveSearchResults.innerHTML = `
        <div class="predictive-search-results__inner" data-search-results>
          <p class="predictive-search-results__no-results">
            ${(typeof Theme !== 'undefined' && /** @type {any} */ (Theme).strings && /** @type {any} */ (Theme).strings.search_no_results)
          ? /** @type {any} */ (Theme).strings.search_no_results
          : 'Geen resultaten gevonden'}
          </p>
        </div>
      `;
      this.#resetScrollPositions();
      return;
    }

    const term = (searchTerm || '').trim();
    const cards = results
      .map((/** @type {any} */ p, /** @type {number} */ idx) => {
        const priceNum = p.price ? Number(p.price) : 0;
        const priceStr = priceNum > 0 ? `€${priceNum.toFixed(2)}` : '';
        const compareNum = p.compare_at_price ? Number(p.compare_at_price) : 0;
        const compareStr = compareNum > 0 && compareNum > priceNum ? `€${compareNum.toFixed(2)}` : '';
        const priceBlock = priceStr
          ? (compareStr
            ? `<span class="predictive-search-results__card-compare-price">${compareStr}</span> <span class="predictive-search-results__card-price">${priceStr}</span>`
            : `<span class="predictive-search-results__card-price">${priceStr}</span>`)
          : '';
        const image = p.featured_image
          ? `<div class="predictive-search-results__card-image-wrapper">
               <img src="${p.featured_image}" alt="${(p.title || '').replace(/"/g, '&quot;')}" loading="lazy" />
             </div>`
          : '';
        const titleHighlighted = this.#highlightMatch(p.title || '', term);
        const subtitle = p.product_type || p.vendor || p.metafields?.producttype || '';
        return `a
          <li
            class="predictive-search-results__card"
            ref="resultsItems[]"
            data-search-result-index="local-results-${idx}"
            on:keydown="/onSearchKeyDown"
          >
            <a class="predictive-search-results__card-link" href="${(p.url || '').replace(/"/g, '&quot;')}">
              ${image}
              <div class="predictive-search-results__card-content">
                <div class="predictive-search-results__card-heading">
                  <span class="predictive-search-results__card-title">${titleHighlighted}</span>
                  ${priceBlock ? `<span class="predictive-search-results__card-price-wrap">${priceBlock}</span>` : ''}
                </div>
                ${subtitle ? `<p class="predictive-search-results__card-vendor">${subtitle.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')}</p>` : ''}
              </div>
            </a>
          </li>
        `;
      })
      .join('');

    predictiveSearchResults.innerHTML = `
      <div class="predictive-search-results__inner" data-search-results>
        <div class="predictive-search-results__wrapper">
          <ul class="predictive-search-results__list predictive-search-results__wrapper-products list-unstyled" role="listbox">
            ${cards}
          </ul>
        </div>
      </div>
    `;

    this.#resetScrollPositions();
  }

  /**
   * Fetch search results using Shopify API products data.
   * Falls back to server predictive search if API fails.
   * @param {string} searchTerm - The term to search for
   */
  async #getSearchResults(searchTerm) {
    const { predictiveSearchResults } = this.refs;
    if (!predictiveSearchResults) return;

    // Try API/local search first
    try {
      const apiResults = await this.#getClientSearchResults(searchTerm);
      if (apiResults && apiResults.length > 0) {
        console.log(`API search: Rendering ${apiResults.length} results for "${searchTerm}"`);
        this.#renderSearchResults(apiResults, searchTerm);
        return;
      } else {
        console.log(`API search: No results found for "${searchTerm}", falling back to server search`);
      }
    } catch (e) {
      console.warn('API search failed, falling back to server predictive search', e);
    }

    // Fallback to server predictive search
    if (!this.dataset.sectionId) return;

    const url = new URL(Theme.routes.predictive_search_url, location.origin);
    url.searchParams.set('q', searchTerm);
    url.searchParams.set('resources[limit_scope]', 'each');

    const abortController = this.#createAbortController();

    sectionRenderer
      .getSectionHTML(this.dataset.sectionId, false, url)
      .then((resultsMarkup) => {
        if (!resultsMarkup) return;

        if (abortController.signal.aborted) return;

        morph(predictiveSearchResults, resultsMarkup);

        this.#resetScrollPositions();
      })
      .catch((error) => {
        if (abortController.signal.aborted) return;
        throw error;
      });
  }

  /**
   * Fetch the markup for the recently viewed products.
   * @returns {Promise<string | null>} The markup for the recently viewed products.
   */
  async #getRecentlyViewedProductsMarkup() {
    if (!this.dataset.sectionId) return null;

    const viewedProducts = RecentlyViewed.getProducts();
    if (viewedProducts.length === 0) return null;

    const url = new URL(Theme.routes.search_url, location.origin);
    url.searchParams.set('q', viewedProducts.map(/** @param {string} id */(id) => `id:${id}`).join(' OR '));
    url.searchParams.set('resources[type]', 'product');

    return sectionRenderer.getSectionHTML(this.dataset.sectionId, false, url);
  }

  #hideResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = true;
  }

  #showResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = false;
  }

  #createAbortController() {
    const abortController = new AbortController();
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }
    this.#activeFetch = abortController;
    return abortController;
  }

  #resetSearch = async () => {
    const { predictiveSearchResults, searchInput } = this.refs;
    const emptySectionId = 'predictive-search-empty';

    this.#currentIndex = -1;
    searchInput.value = '';
    this.#hideResetButton();

    const abortController = this.#createAbortController();
    const url = new URL(window.location.href);
    url.searchParams.delete('page');

    const emptySectionMarkup = await sectionRenderer.getSectionHTML(emptySectionId, false, url);
    const parsedEmptySectionMarkup = new DOMParser()
      .parseFromString(emptySectionMarkup, 'text/html')
      .querySelector('.predictive-search-empty-section');

    if (!parsedEmptySectionMarkup) throw new Error('No empty section markup found');

    /** This needs to be awaited and not .then so the DOM is already morphed
     * when #closeResults is called and therefore the height is animated */
    const viewedProducts = RecentlyViewed.getProducts();

    if (viewedProducts.length > 0) {
      const recentlyViewedMarkup = await this.#getRecentlyViewedProductsMarkup();
      if (!recentlyViewedMarkup) return;

      const parsedRecentlyViewedMarkup = new DOMParser().parseFromString(recentlyViewedMarkup, 'text/html');
      const recentlyViewedProductsHtml = parsedRecentlyViewedMarkup.getElementById('predictive-search-products');
      if (!recentlyViewedProductsHtml) return;

      for (const child of recentlyViewedProductsHtml.children) {
        if (child instanceof HTMLElement) {
          child.setAttribute('ref', 'recentlyViewedWrapper');
        }
      }

      const collectionElement = parsedEmptySectionMarkup.querySelector('#predictive-search-products');
      if (!collectionElement) return;
      collectionElement.prepend(...recentlyViewedProductsHtml.children);
    }

    if (abortController.signal.aborted) return;

    morph(predictiveSearchResults, parsedEmptySectionMarkup);
    this.#resetScrollPositions();
  };
}

if (!customElements.get('predictive-search-component')) {
  customElements.define('predictive-search-component', PredictiveSearchComponent);
}
